import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'

import '@fontsource-variable/plus-jakarta-sans/wght.css'
import '@fontsource-variable/plus-jakarta-sans/wght-italic.css'

import './global.css'

import App from './App.vue'
import { isBlockPreviewPathname } from './blockPreviewPath'
import router from './router'
import i18n from './i18n'
import { applyI18nLocaleForCurrentPath } from './uiLocalePreference'
import { useAuthStore } from './stores/auth'
import { useContentStore } from './stores/content'
import { setupGoogleAnalytics } from './analytics/googleAnalytics'
import { useSiteConfigStore } from './stores/siteConfig'

const ROBOTS_META_ID = 'site-config-robots'

/** Apply or remove noindex meta from runtime site-config (google_index). */
function applyRobotsMetaFromGoogleIndex(googleIndex: boolean) {
  const existing = document.getElementById(ROBOTS_META_ID)
  if (googleIndex) {
    existing?.remove()
    return
  }
  if (existing) return
  const meta = document.createElement('meta')
  meta.id = ROBOTS_META_ID
  meta.name = 'robots'
  meta.content = 'noindex, nofollow'
  document.head.appendChild(meta)
}
import { registerSW } from 'virtual:pwa-register'
import { initSwUpdateOrchestrator } from '@/pwa/swUpdateOrchestrator'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(i18n)
app.use(router)

const skipSiteBootstrap = isBlockPreviewPathname(window.location.pathname)

// vue-i18n: URL prefix wins; on unprefixed (tet) URLs keep stored en/pt when user did not switch to tet.
// document.lang follows the URL tree (content locale), not necessarily UI strings.
router.afterEach((to) => {
  applyI18nLocaleForCurrentPath(to.path)
})

// Revalidate site listing in background on each navigation so new pages appear
router.afterEach((to) => {
  if (to.meta.blockPreview || isBlockPreviewPathname(to.path)) return
  const contentStore = useContentStore(pinia)
  void contentStore.syncCurrentRoute(to.path, to.query as Record<string, unknown>)
  void contentStore.revalidateSite()
})

const initialPath = window.location.pathname
applyI18nLocaleForCurrentPath(initialPath)

const contentStore = useContentStore(pinia)
const authStore = useAuthStore(pinia)
const siteConfigStore = useSiteConfigStore(pinia)
// Default: noindex until site-config confirms GOOGLE_INDEX (matches Django default).
applyRobotsMetaFromGoogleIndex(false)

void siteConfigStore.initialize().then(() => {
  applyRobotsMetaFromGoogleIndex(siteConfigStore.googleIndex)
  if (!skipSiteBootstrap && siteConfigStore.googleAnalyticsTag) {
    setupGoogleAnalytics(siteConfigStore.googleAnalyticsTag, router, {
      debugMode: siteConfigStore.googleAnalyticsDebugMode,
    })
  }
})

if (!skipSiteBootstrap) {
  // Re-sync route as soon as cached listing becomes available so first-load pages
  // can render from cache before any delayed network refresh.
  watch(
    () => contentStore.data?.items?.length ?? 0,
    (count, prevCount) => {
      if (count > 0 && prevCount === 0) {
        void contentStore.syncCurrentRoute(
          router.currentRoute.value.path,
          router.currentRoute.value.query as Record<string, unknown>
        )
      }
    }
  )

  void contentStore.initialize()

  // Keep an eager sync for initial preview query handling while bootstrap is in progress.
  void contentStore.syncCurrentRoute(
    router.currentRoute.value.path,
    router.currentRoute.value.query as Record<string, unknown>
  )
  authStore.initialize()
}

// Register SW only in production; keep Vite dev free of SW side-effects.
if (import.meta.env.PROD) {
  registerSW({ immediate: true })
  initSwUpdateOrchestrator(router)
}

app.mount('#app')
